/**
 * The financial year of a workspace (L42 of docs/plans/ledger.md): its
 * stored form, the rule OS sets it by, and the dates it cuts.
 *
 * `app.tenant.fiscal_year_start` holds a month and a day as `MM-DD`. OS sets
 * it during setup:
 *
 *   1. from the country of the business, through `TAX_COUNTRIES`;
 *   2. from evidence, when there is some: a file named "2025-26", a document
 *      series, or an accountant's report;
 *   3. by one question where companies choose their own year (the UK, the
 *      US, Canada), where the evidence disagrees with the table, or where the
 *      country is not in the table.
 *
 * A change is free until the first period close; after it, a change takes
 * effect only from the next year's start (`fiscalYearChange`). Calendars that
 * are not Gregorian are an open item. A day after the 28th is refused, so
 * every year holds the start day.
 */
import { formatNumber } from './format';
import { t } from './i18n/t';
import { taxCountryOf, type FiscalYearStart } from './tax-countries';

export type { FiscalYearStart } from './tax-countries';

const TEXT = /^(\d{2})-(\d{2})$/;

/** True for a start every year holds: a month 1 to 12 and a day 1 to 28. */
export const isFiscalYearStart = (value: unknown): value is FiscalYearStart => {
  if (typeof value !== 'object' || value === null) return false;
  const { month, day } = value as Record<string, unknown>;
  return Number.isInteger(month) && Number.isInteger(day) && (month as number) >= 1 && (month as number) <= 12 && (day as number) >= 1 && (day as number) <= 28;
};

/** The stored form: `04-01` for 1 April. */
export const fiscalYearStartText = (start: FiscalYearStart): string =>
  `${String(start.month).padStart(2, '0')}-${String(start.day).padStart(2, '0')}`;

/** The start a stored `MM-DD` holds, or null. */
export const parseFiscalYearStart = (text: string | null | undefined): FiscalYearStart | null => {
  if (typeof text !== 'string') return null;
  const match = TEXT.exec(text.trim());
  if (match === null) return null;
  const start = { month: Number(match[1]), day: Number(match[2]) };
  return isFiscalYearStart(start) ? start : null;
};

export const sameFiscalYearStart = (a: FiscalYearStart | null, b: FiscalYearStart | null): boolean =>
  a !== null && b !== null && a.month === b.month && a.day === b.day;

/** The month and day the year ends on: the day before the start (31 March for an April year). */
export const fiscalYearEnd = (start: FiscalYearStart): FiscalYearStart => {
  if (start.day > 1) return { month: start.month, day: start.day - 1 };
  const month = start.month === 1 ? 12 : start.month - 1;
  // February's last day is its 28th or 29th; a year ending in February names the month alone.
  const last = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1]!;
  return { month, day: last };
};

const iso = (year: number, start: FiscalYearStart): string =>
  `${String(year).padStart(4, '0')}-${String(start.month).padStart(2, '0')}-${String(start.day).padStart(2, '0')}`;

/** The year a day's financial year starts in: 2026 for 22 Oct 2026 under an April year, 2025 for 2 Feb 2026. */
export const fiscalYearOfDay = (day: string, start: FiscalYearStart): number => {
  const year = Number(day.slice(0, 4));
  const month = Number(day.slice(5, 7));
  const date = Number(day.slice(8, 10));
  return month < start.month || (month === start.month && date < start.day) ? year - 1 : year;
};

/** The first day of a day's financial year, and the first day of the next one (`YYYY-MM-DD`, the end exclusive). */
export const fiscalYearBounds = (day: string, start: FiscalYearStart): { readonly from: string; readonly until: string } => {
  const year = fiscalYearOfDay(day, start);
  return { from: iso(year, start), until: iso(year + 1, start) };
};

/** The first day of the financial year after the one `day` is in. */
export const nextFiscalYearStartDay = (day: string, start: FiscalYearStart): string => fiscalYearBounds(day, start).until;

/* --------------------------------------------------------- the change rule */

/**
 * What a change to the year does (L42): free until the first close, so the
 * new start holds from now; after a close, the year in progress keeps its
 * start and the new one holds from that year's end.
 */
export type FiscalYearChange =
  | { readonly kind: 'now'; readonly start: FiscalYearStart }
  | { readonly kind: 'from'; readonly start: FiscalYearStart; readonly from: string }
  | { readonly kind: 'same' };

export const fiscalYearChange = (input: {
  readonly held: FiscalYearStart | null;
  readonly next: FiscalYearStart;
  /** Whether the workspace has closed a period (`app.close_period`). */
  readonly closed: boolean;
  /** The business's today, `YYYY-MM-DD`. */
  readonly today: string;
}): FiscalYearChange => {
  if (sameFiscalYearStart(input.held, input.next)) return { kind: 'same' };
  if (!input.closed || input.held === null) return { kind: 'now', start: input.next };
  return { kind: 'from', start: input.next, from: nextFiscalYearStartDay(input.today, input.held) };
};

/** The start in force on `today`: a pending start once its day has come, else the held one. */
export const fiscalYearStartOn = (
  held: FiscalYearStart | null,
  pending: { readonly start: FiscalYearStart; readonly from: string } | null,
  today: string,
): FiscalYearStart | null => (pending !== null && today >= pending.from ? pending.start : held);

/* ------------------------------------------------------------- the evidence */

/**
 * What a piece of evidence says about the year. A span name (2025-26) says
 * only that the year crosses the calendar's; a series or a report gives the start.
 */
export type FiscalYearEvidence =
  | { readonly kind: 'span'; readonly text: string }
  | { readonly kind: 'start'; readonly start: FiscalYearStart; readonly from: 'series' | 'report' | 'said' };

/** "2025-26", "FY 2025-26", "2025/26", "2025-2026", "FY25-26": two years in a row, named as one year. */
const SPAN = /(?:^|[^0-9])(?:fy\s*)?((?:19|20)\d{2}|\d{2})\s*([-/–_])\s*((?:19|20)?\d{2})(?![0-9])/i;

/** "FY25", "FY 2026": one year named as a financial year. */
const FY_ONE = /(?:^|[^a-z0-9])(fy\s*'?(?:(?:19|20)\d{2}|\d{2}))(?![0-9])(?!\s*[-/–_]\s*\d)/i;

/**
 * Words whose number is a thing's, not a year's: "Batch 24-25", "Room 12-13",
 * "stock_25-26", "Class 11-12", "Plot no. 20-21". A span right after one of
 * them is not read as a year, unless FY says it is.
 */
const NUMBERED_THING = new Set([
  'batch', 'room', 'rooms', 'stock', 'lot', 'flat', 'unit', 'plot', 'block', 'bed', 'table', 'item', 'items',
  'class', 'std', 'standard', 'grade', 'section', 'roll', 'seat', 'rack', 'shelf', 'size', 'ward', 'house',
  'floor', 'no', 'num', 'number', 'pg', 'page', 'pages',
]);

/** The word just before `at` in a name, lower case, "" when none: separators and dots do not count. */
const wordBefore = (name: string, at: number): string => name.slice(0, at).toLowerCase().split(/[^a-z]+/).filter(Boolean).at(-1) ?? '';

/**
 * The span a file's name holds, as evidence, or null. Read only when the two
 * numbers are years that follow each other ("24-25", "2024-25", or "FY25"
 * alone), when no word like Batch, Room or Stock comes right before them, and
 * when they are not two parts of a date ("05-06-2024").
 */
export const fiscalYearEvidenceInName = (name: string): FiscalYearEvidence | null => {
  const match = SPAN.exec(name);
  if (match === null) {
    const one = FY_ONE.exec(name);
    return one === null ? null : { kind: 'span', text: one[1]!.trim() };
  }
  const first = match[1]!;
  const second = match[3]!;
  const a = Number(first.length === 2 ? `20${first}` : first);
  const b = Number(second.length === 2 ? `${String(a).slice(0, 2)}${second}` : second);
  if (b !== a + 1) return null;
  // A third number after them, by the same mark, makes a date of the three ("05-06-2024");
  // a series number after another mark does not ("ST/26-27/0418").
  const after = name.slice(match.index + match[0].length);
  if (after.startsWith(match[2]!) && /^\d{2}(?:\d{2})?(?![0-9])/.test(after.slice(1))) return null;
  const said = /fy/i.test(match[0]);
  const start = match.index + (/^[^0-9a-z]/i.test(match[0]) ? 1 : 0);
  if (!said && NUMBERED_THING.has(wordBefore(name, start))) return null;
  return { kind: 'span', text: match[0].replace(/^[^0-9a-z]/i, '').trim() };
};

/* --------------------------------------------------------------- the choice */

export type FiscalYearAsk = 'companies_choose' | 'evidence_disagrees' | 'unknown_country';

/**
 * What OS does about the year: set it, or ask one question with a
 * suggestion. `source` says what set it.
 */
export type FiscalYearChoice =
  | { readonly kind: 'set'; readonly start: FiscalYearStart; readonly source: 'country' | 'evidence' }
  | { readonly kind: 'ask'; readonly why: FiscalYearAsk; readonly suggested: FiscalYearStart };

const JANUARY: FiscalYearStart = { month: 1, day: 1 };

/** The rule of L42: the person's own answer first, then the evidence and the table. */
export const chooseFiscalYear = (input: {
  /** ISO 3166-1 alpha-2, or null when the country is not known. */
  readonly country: string | null;
  readonly evidence?: readonly FiscalYearEvidence[];
}): FiscalYearChoice => {
  const row = taxCountryOf(input.country);
  const evidence = input.evidence ?? [];
  const said = evidence.find((one): one is Extract<FiscalYearEvidence, { kind: 'start' }> => one.kind === 'start' && one.from === 'said');
  if (said !== undefined) return { kind: 'set', start: said.start, source: 'evidence' };
  const starts = evidence.flatMap((one) => (one.kind === 'start' ? [one.start] : []));
  const spans = evidence.some((one) => one.kind === 'span');
  const table = row?.fiscalYearStart ?? null;

  // Starts that disagree with each other settle nothing.
  const first = starts[0] ?? null;
  if (first !== null && starts.some((one) => !sameFiscalYearStart(one, first))) {
    return { kind: 'ask', why: 'evidence_disagrees', suggested: table ?? first };
  }
  if (first !== null) {
    if (spans && first.month === 1 && first.day === 1) return { kind: 'ask', why: 'evidence_disagrees', suggested: table ?? first };
    if (table === null || row!.yearChosen || sameFiscalYearStart(table, first)) return { kind: 'set', start: first, source: 'evidence' };
    return { kind: 'ask', why: 'evidence_disagrees', suggested: first };
  }
  if (row === null || table === null) return { kind: 'ask', why: 'unknown_country', suggested: JANUARY };
  // A span names a year across two calendar years: it agrees with any start but 1 January.
  const spanAgrees = !spans || !(table.month === 1 && table.day === 1);
  if (row.yearChosen) return { kind: 'ask', why: 'companies_choose', suggested: table };
  if (!spanAgrees) return { kind: 'ask', why: 'evidence_disagrees', suggested: table };
  return { kind: 'set', start: table, source: 'country' };
};

/* ---------------------------------------------------------------- the words */

type MonthKey = `money_settings.month.${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12}`;
const monthWord = (month: number): string => t(`money_settings.month.${month}` as MonthKey);

/** "April to March"; a start off the 1st names its days: "6 April to 5 April". */
export const fiscalYearSpanWords = (start: FiscalYearStart): string => {
  const end = fiscalYearEnd(start);
  if (start.day === 1) return t('money_settings.year.span', { from: monthWord(start.month), to: monthWord(end.month) });
  return t('money_settings.year.span', { from: `${formatNumber(start.day)} ${monthWord(start.month)}`, to: `${formatNumber(end.day)} ${monthWord(end.month)}` });
};

/** The one line OS says once it sets the year: "Your financial year runs April to March." */
export const fiscalYearLine = (start: FiscalYearStart): string => t('money_settings.year.runs', { span: fiscalYearSpanWords(start) });
